import React, { useState, useEffect } from 'react';
import { Search, X, Zap, Gift, DollarSign, Bookmark, Users, LogIn, ChevronDown, User, Package } from 'lucide-react';
import { initializeApp } from 'firebase/app';
import { getDatabase, ref, onValue } from 'firebase/database';

// Firebase config
const firebaseConfig = {
  apiKey: "AIzaSyARv3RMmZCBVru7_sjwxNDt9HO5PxbAtx4",
  authDomain: "cheeks-live-status.firebaseapp.com",
  databaseURL: "https://cheeks-live-status-default-rtdb.europe-west1.firebasedatabase.app",
  projectId: "cheeks-live-status",
  storageBucket: "cheeks-live-status.firebasestorage.app",
  messagingSenderId: "743249460847",
  appId: "1:747249460847:web:46f2d8835c19ca8effd79a"
};

// Initialise Firebase
const app = initializeApp(firebaseConfig);
const database = getDatabase(app);

const CardExplorer = () => {
  const [isLive, setIsLive] = useState(false);
  const [memberCount, setMemberCount] = useState(null);
  const [voiceCount, setVoiceCount] = useState(0);
  const [instagramCount, setInstagramCount] = useState(null);
  const [whatnotCount, setWhatnotCount] = useState(null);
  const [bannerText, setBannerText] = useState('');
  const [myStatus, setMyStatus] = useState('');
  const [myPresence, setMyPresence] = useState(null);
  const [myBadges, setMyBadges] = useState([]);
  const [myActivity, setMyActivity] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [discordUser, setDiscordUser] = useState(() => {
    try {
      const saved = localStorage.getItem('discordUser');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [loginError, setLoginError] = useState(false);
  const [loginErrorVisible, setLoginErrorVisible] = useState(false);
  const [showPogoModal, setShowPogoModal] = useState(false);
  const [funFact, setFunFact] = useState('');

  useEffect(() => {
    fetch('https://pokefacts.vercel.app/')
      .then(r => r.json())
      .then(d => setFunFact(d.data?.[0] || ''))
      .catch(() => {});
  }, []);

  // Badge definitions — keyed by role name, with image path and display label

  useEffect(() => {
    if (!loginError) return;
    setLoginErrorVisible(true);
    const fade = setTimeout(() => setLoginErrorVisible(false), 4500);
    const clear = setTimeout(() => setLoginError(false), 5000);
    return () => { clearTimeout(fade); clearTimeout(clear); };
  }, [loginError]);

  // Tab state — 'profile' or 'inventory'
  const [activeTab, setActiveTab] = useState('profile');

  const [searchQuery, setSearchQuery] = useState('');
  const [showVideoModal, setShowVideoModal] = useState(false);
  const bannerBubbleRef = React.useRef(null);
  const [videoEnded, setVideoEnded] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [images, setImages] = useState([]);

  const [currentExampleIndex, setCurrentExampleIndex] = useState(0);
  const [currentText, setCurrentText] = useState('');
  const [isTyping, setIsTyping] = useState(true);

  const examples = ['Pikachu', 'Giratina', 'Sunflora'];

  const categories = [
    { id: 'all', name: 'All Items', folder: null },
    { id: 'tcg', name: 'Pokemon TCG', folder: 'TCG' },
    { id: 'nontcg', name: 'Non-TCG', folder: 'nonTCG' },
  ];

  const handleDiscordLogin = () => {
    const CLIENT_ID = '1360587565190746193';
    const REDIRECT_URI = encodeURIComponent(window.location.origin);
    const SCOPE = encodeURIComponent('identify guilds');
    const AUTH_URL = `https://discord.com/api/oauth2/authorize?client_id=${CLIENT_ID}&redirect_uri=${REDIRECT_URI}&response_type=token&scope=${SCOPE}`;
    window.location.href = AUTH_URL;
  };

  const handleLogout = () => {
    localStorage.removeItem('discordUser');
    setDiscordUser(null);
    setShowUserMenu(false);
  };

  useEffect(() => {
    if (!showUserMenu) return;
    const handler = (e) => {
      if (!e.target.closest('[data-user-menu]')) setShowUserMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showUserMenu]);

  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes('access_token')) {
      const params = new URLSearchParams(hash.replace('#', '?'));
      const token = params.get('access_token');
      window.history.replaceState({}, document.title, window.location.pathname);
      fetch('https://discord.com/api/users/@me', {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(res => res.json())
        .then(userData => {
          return fetch('https://discord.com/api/users/@me/guilds', {
            headers: { Authorization: `Bearer ${token}` }
          })
            .then(res => res.json())
            .then(guilds => {
              const isMember = guilds.some(g => g.id === '1466528743265931459');
              if (isMember) {
                localStorage.setItem('discordUser', JSON.stringify(userData));
                setDiscordUser(userData);
              } else {
                setLoginError(true);
              }
            });
        })
        .catch(err => console.error("Login failed:", err));
    }
  }, []);

  useEffect(() => {
    const liveRef = ref(database, 'isLive');
    const unsubscribe = onValue(liveRef, (snapshot) => setIsLive(snapshot.val() === true));
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const memberRef = ref(database, 'memberCount');
    const unsubscribe = onValue(memberRef, (snapshot) => {
      const value = snapshot.val();
      if (value !== null && value !== undefined) setMemberCount(value);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const voiceRef = ref(database, 'voiceCount');
    const unsubscribe = onValue(voiceRef, (snapshot) => setVoiceCount(snapshot.val() || 0));
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const igRef = ref(database, 'instagramCount');
    const unsubscribe = onValue(igRef, (snapshot) => {
      const value = snapshot.val();
      if (value !== null && value !== undefined) setInstagramCount(value);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const wnRef = ref(database, 'whatnotCount');
    const unsubscribe = onValue(wnRef, (snapshot) => {
      const value = snapshot.val();
      if (value !== null && value !== undefined) setWhatnotCount(value);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const bannerRef = ref(database, 'bannerText');
    const unsubscribe = onValue(bannerRef, (snapshot) => setBannerText(snapshot.val() || ''));
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const statusRef = ref(database, 'myStatus');
    const unsubscribe = onValue(statusRef, (snapshot) => {
      setMyStatus(snapshot.val() || '');
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const presenceRef = ref(database, 'myPresence');
    const unsubscribe = onValue(presenceRef, (snapshot) => {
      setMyPresence(snapshot.val() || null);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const badgesRef = ref(database, 'myBadges');
    const unsubscribe = onValue(badgesRef, (snapshot) => {
      setMyBadges(snapshot.val() || []);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const activityRef = ref(database, 'myActivity');
    const unsubscribe = onValue(activityRef, (snapshot) => {
      setMyActivity(snapshot.val() || null);
    });
    return () => unsubscribe();
  }, []);


  const [elapsed, setElapsed] = useState('');
  useEffect(() => {
    if (!myActivity?.startedAt) { setElapsed(''); return; }
    const tick = () => {
      const secs = Math.floor((Date.now() - myActivity.startedAt) / 1000);
      const h = Math.floor(secs / 3600);
      const m = Math.floor((secs % 3600) / 60);
      const sc = secs % 60;
      setElapsed(h > 0
        ? `${h}:${String(m).padStart(2,'0')}:${String(sc).padStart(2,'0')}`
        : `${m}:${String(sc).padStart(2,'0')}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [myActivity?.startedAt]);

  const formatCount = (count) => {
    if (!count) return null;
    return count.toLocaleString();
  };

  useEffect(() => {
    // TCG Images
const tcgFileList = [
  'Articuno Holo - Fossil - Japanese.png',
  'Blissey Holo - Neo Revelation - Japanese.png',
  'Charizard EX SIR - Pokemon 151 - English.png',
  'Charizard Rainbow - Brilliant Stars - English.png',
  'Charizard Rev Holo - Premium File Promo - Japanese.png',
  'Dark Raichu Holo - Team Rocket WOTC - English.png',
  'Eevee EX Promo - Prismatic Evolutions UPC - English.png',
  'Eevee EX SAR - Terastal Festival - Japanese.png',
  'Entei Rev Holo - Premium File Promo - Japanese.png',
  'Felt Hat Pikachu - Promo - English.png',
  'Gengar VMAX Alt Art 1 - Fusion Strike - English.png',
  'Gengar VMAX Alt Art 2 - Fusion Strike - English.png',
  'Gengar VMAX Alt Art 3 - Fusion Strike - English.png',
  'Giratina V Alt Art - Lost Origin - English.png',
  'Haunter Holo - Fossil - Japanese.png',
  'Lugia Holo - Neo Genesis - Japanese.png',
  'Lugia V Alt Art - Silver Tempest - English.png',
  'Mega Charizard EX - Inferno X - Japanese.png',
  'Mega Charizard EX Promo 1 - Charizard UPC - English.png',
  'Mega Charizard EX Promo 2 - Charizard UPC - English.png',
  'Mega Charizard EX SIR - Phantasmal Flames - English.png',
  'Mew EX - Pokemon 151 - Japanese.png',
  'Mew Holo - WOTC Promo - English.png',
  'Moltres Holo - Fossil - Japanese.png',
  'Moltres V Alt Art - Chilling Reign - English.png',
  'Pichu Rev Holo - Premium File Promo - Japanese.png',
  'Rayquaza VMAX 1 - Silver Tempest - English.png',
  'Rayquaza VMAX 2 - Silver Tempest - English.png',
  'Spiky Eared Pichu Holo - Legend of Arceus Promo - Japanese.png',
  'Togepi Rev Holo - Southern Islands Promo - Japanese.png',
  'Umbreon VMAX Alt Art - Evolving Skies - English.png',
];

    // Non-TCG Images (plushies, figures, sealed products, etc.)
const nonTCGFileList = [
  'Batomen - Series 1 - TOMY.jpg',
  'Batomen - Series 2 - TOMY.jpg',
  'Batomen - Series 3 - TOMY.jpg',
  'Battle Memo - Part 1 - TOMY.jpg',
  'Battle Memo - Part 2 - TOMY.jpg',
  'Battle Memo - Part 3 - TOMY.jpg',
  'Blastoise  - Batomen - TOMY.jpg',
  'Charizard - Batomen - TOMY.jpg',
  'Charmander - Topsun Blue Back - Japanese.png',
  'Coonuts - Series 3 - Bandai.jpg',
  'Gengar Clear Promo - PokeMaze - Pokemon Center Japan.jpg',
  'Mew Holo - Batomen - TOMY.jpg',
  'Mewtwo Holo - Batomen - TOMY.jpg',
  'Pikachu - Pixel Coaster - Pokemon Center Japan.jpg',
  'Pikachu - Pokepeace - Plushy Notepad.jpg',
  'Pikachu & Eevee - PokeGET.jpg',
  'Snorlax - Bandai Anime - Carddass.jpg',
  'Umbreon Clear Promo - PokeMaze - Pokemon Center Japan.jpg',
  'Venusaur  - Batomen - TOMY.jpg',
];

    const tcgImages = tcgFileList.map((filename, index) => {
      const nameWithoutExt = filename.replace(/\.[^/.]+$/, '');
      let parts = nameWithoutExt.split('-').map(p => p.trim()).filter(p => p);
      let gradeLabel = null;
      const gradingCompanies = ['PSA', 'BGS', 'ACE', 'CGC'];
      const firstPart = parts[0];
      const weightMatch = firstPart.match(/^(\d+\.?\d*g)\s+(.+)/i);
      if (weightMatch) {
        gradeLabel = weightMatch[1];
        parts[0] = weightMatch[2];
      } else {
        for (const company of gradingCompanies) {
          if (firstPart.toUpperCase().startsWith(company)) {
            const gradeMatch = firstPart.match(new RegExp(`${company}\\s*([0-9.]+)\\s*(.*)`, 'i'));
            if (gradeMatch) {
              gradeLabel = `${company} ${gradeMatch[1]}`;
              parts[0] = gradeMatch[2].trim();
              if (!parts[0]) parts.shift();
            }
            break;
          }
        }
      }
      const cardName = parts[0] || 'Unknown Card';
      const setLines = parts.slice(1);
      return { id: index + 1, name: cardName, set: setLines.join('  '), filename, category: 'tcg', grade: gradeLabel, folder: 'TCG' };
    });

    const nonTCGImages = nonTCGFileList.map((filename, index) => {
      const nameWithoutExt = filename.replace(/\.[^/.]+$/, '');
      let parts = nameWithoutExt.split('-').map(p => p.trim()).filter(p => p);
      let gradeLabel = null;
      const firstPart = parts[0];
      const weightMatch = firstPart.match(/^(\d+\.?\d*g)\s+(.+)/i);
      if (weightMatch) {
        gradeLabel = weightMatch[1];
        parts[0] = weightMatch[2];
      }
      const itemName = parts[0] || 'Unknown Item';
      const itemLines = parts.slice(1);
      return { id: tcgImages.length + index + 1, name: itemName, set: itemLines.join('  '), filename, category: 'nontcg', grade: gradeLabel, folder: 'nonTCG' };
    });

    setImages([...tcgImages, ...nonTCGImages]);
  }, []);

  useEffect(() => {
    const currentExample = examples[currentExampleIndex];
    if (isTyping) {
      if (currentText.length < currentExample.length) {
        const timeout = setTimeout(() => setCurrentText(currentExample.substring(0, currentText.length + 1)), 100);
        return () => clearTimeout(timeout);
      } else {
        const timeout = setTimeout(() => setIsTyping(false), 2000);
        return () => clearTimeout(timeout);
      }
    } else {
      if (currentText.length > 0) {
        const timeout = setTimeout(() => setCurrentText(currentText.substring(0, currentText.length - 1)), 50);
        return () => clearTimeout(timeout);
      } else {
        const timeout = setTimeout(() => {
          setCurrentExampleIndex((prevIndex) => (prevIndex + 1) % examples.length);
          setIsTyping(true);
        }, 500);
        return () => clearTimeout(timeout);
      }
    }
  }, [currentText, isTyping, currentExampleIndex]);

  useEffect(() => {
    const video = document.createElement('video');
    video.src = '/splash.mp4';
    video.preload = 'auto';
    video.load();
  }, []);

  // Prevent right-click / long-press saving of images
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      img { -webkit-user-select: none; user-select: none; -webkit-touch-callout: none; pointer-events: none; }
      a img, button img { pointer-events: auto; }
      * { -webkit-touch-callout: none; }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // Live clock
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);
  useEffect(() => {
    if (activeTab !== 'profile') return;
    const container = bannerBubbleRef.current;
    if (!container) return;
    const bubbles = [];
    for (let i = 0; i < 22; i++) {
      const b = document.createElement('div');
      b.className = 'banner-bubble';
      const size = Math.random() * 20 + 12;
      b.style.width = `${size}px`;
      b.style.height = `${size}px`;
      b.style.left = `${Math.random() * 100}%`;
      b.style.animationDelay = `-${Math.random() * 8}s`;
      b.style.animationDuration = `${Math.random() * 4 + 5}s`;
      b.style.setProperty('--bdrift', Math.random() * 2 - 1);
      container.appendChild(b);
      bubbles.push(b);
    }
    return () => bubbles.forEach(b => b.remove());
  }, [activeTab]);

  const handleVideoEnd = () => setVideoEnded(true);
  const handleVideoModalClose = () => {
    setShowVideoModal(false);
    setVideoEnded(false);
  };

  const handleSearch = () => { /* filter is reactive */ };

  const filteredImages = images.filter(img => {
    if (!searchQuery) return selectedCategory === 'all' || img.category === selectedCategory;
    const searchWords = searchQuery.toLowerCase().trim().split(/\s+/);
    const searchableText = [img.name, img.set, img.filename, img.grade || ''].join(' ').toLowerCase();
    const matchesSearch = searchWords.every(word => searchableText.includes(word));
    const matchesCategory = selectedCategory === 'all' || img.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // ── Profile link data ──────────────────────────────────────────────
  const profileLinks = [
    {
      label: 'Discord',
      sub: 'discord.cheeks.uk',
      href: 'https://cheeks.uk/docs/',
      color: '#5865F2',
      bgColor: 'rgba(88,101,242,0.12)',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M20.317 4.37a19.791 19.791 0 00-4.885-1.515.074.074 0 00-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 00-5.487 0 12.64 12.64 0 00-.617-1.25.077.077 0 00-.079-.037A19.736 19.736 0 003.677 4.37a.07.07 0 00-.032.027C.533 9.046-.32 13.58.099 18.057c.001.022.015.043.032.054a19.9 19.9 0 005.993 3.03.078.078 0 00.084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 00-.041-.106 13.107 13.107 0 01-1.872-.892.077.077 0 01-.008-.128 10.2 10.2 0 00.372-.292.074.074 0 01.077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 01.078.01c.12.098.246.198.373.292a.077.077 0 01-.006.127 12.299 12.299 0 01-1.873.892.077.077 0 00-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 00.084.028 19.839 19.839 0 006.002-3.03.077.077 0 00.032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 00-.031-.03z"/>
        </svg>
      )
    },
    {
      label: 'Instagram',
      sub: '@togecheeks',
      href: 'https://www.instagram.com/togecheeks',
      color: '#E1306C',
      bgColor: 'rgba(225,48,108,0.12)',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
        </svg>
      )
    },
    {
      label: 'Whatnot — Live Auctions',
      sub: 'Watch & bid live',
      href: 'https://www.whatnot.com/user/togecheeks/shows',
      color: '#FADC00',
      bgColor: 'rgba(250,220,0,0.1)',
      isButton: true,
      icon: <Gift className="h-5 w-5" />
    },
    {
      label: 'Get £10 FREE on Whatnot',
      sub: 'New buyer promo',
      href: 'https://whatnot.com/invite/togecheeks',
      color: '#FADC00',
      bgColor: 'rgba(250,220,0,0.1)',
      icon: <DollarSign className="h-5 w-5" />
    },
    {
      label: 'Bookmark Next Livestreams',
      sub: 'Never miss a drop',
      href: 'https://www.whatnot.com/user/togecheeks/shows',
      color: '#FADC00',
      bgColor: 'rgba(250,220,0,0.1)',
      icon: <Bookmark className="h-5 w-5" />
    },
  ];

  const badgeMap = {
    diamond: { img: '/diamond.png', label: 'Diamond' },
  };

  const badges = (Array.isArray(myBadges) ? myBadges : Object.keys(myBadges || {}))
    .map(key => badgeMap[key])
    .filter(Boolean);

  return (
    <div
      className="bg-slate-900 flex flex-col"
      style={{ minHeight: '100svh' }}
      onContextMenu={(e) => e.preventDefault()}
      onDragStart={(e) => e.preventDefault()}
    >

      {/* ── BANNER ── */}
      {bannerText && (
        <div className="relative z-50 flex items-center justify-center py-2 px-4 bg-slate-800/80 backdrop-blur-lg border-b border-slate-700/50">
          <div className="flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full px-4 py-1.5 shadow-sm">
            <img src="/pball.svg" alt="" className="h-3 w-3 animate-spin flex-shrink-0" style={{animationDuration:'0.8s', filter:'brightness(0) invert(1)'}} />
            <span className="text-white text-[11px] font-medium tracking-wide leading-none">
              {(() => {
                const linkRegex = /\[(.*?)\]\((.*?)\)/;
                const match = bannerText.match(linkRegex);
                if (match) {
                  const [fullMatch, label, url] = match;
                  const [before, after] = bannerText.split(fullMatch);
                  return (
                    <>
                      {before}
                      <a href={url} className="ml-1.5 bg-white/20 hover:bg-white/40 px-2 py-0.5 rounded-full text-[10px] transition-all border border-white/20 inline-flex items-center hover:scale-105 active:scale-95 font-semibold">
                        {label}
                      </a>
                      {after}
                    </>
                  );
                }
                return bannerText;
              })()}
            </span>
          </div>
        </div>
      )}

      {/* ── TOP NAV ── */}
      <div className="bg-slate-800/80 backdrop-blur-lg border-b border-slate-700/50 relative z-[100]">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">

            {/* Left: live status + login */}
            <div className="flex items-center gap-2 flex-1">
              <div className="flex flex-col items-stretch gap-1 w-[120px]">
                <a href="https://www.whatnot.com/user/togecheeks/shows" target="_blank" rel="noopener noreferrer" className="group">
                </a>

                {!discordUser ? (
                  <div className="relative">
                    <button
                      onClick={handleDiscordLogin}
                      className="flex items-center justify-center space-x-1.5 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white px-2.5 py-1 rounded-md transition-all duration-200 active:scale-95 w-full"
                    >
                      <LogIn className="h-3.5 w-3.5 flex-shrink-0" />
                      <span className="text-[11px] font-bold uppercase tracking-wider">Login</span>
                    </button>
                    {loginError && (
                      <div
                        className="absolute top-full left-0 mt-1.5 w-48 rounded-lg px-2.5 py-2 shadow-xl z-[999] transition-opacity duration-500"
                        style={{
                          backgroundColor: 'rgba(15,15,20,0.92)',
                          border: '1px solid rgba(255,102,102,0.4)',
                          opacity: loginErrorVisible ? 1 : 0
                        }}
                      >
                        <p className="text-[10px] leading-snug" style={{ color: '#FF6666' }}>You must be a member of Cheekscord to login.</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="relative w-full" data-user-menu>
                    <button
                      onClick={() => setShowUserMenu(prev => !prev)}
                      className="flex items-center justify-center space-x-1.5 bg-slate-700/50 hover:bg-slate-600/50 border border-slate-600 text-white px-2.5 py-1 rounded-md transition-all duration-200 w-full"
                    >
                      <img
                        src={`https://cdn.discordapp.com/avatars/${discordUser.id}/${discordUser.avatar}.png`}
                        alt="User"
                        className="h-4 w-4 rounded-full flex-shrink-0"
                      />
                      <span className="text-[11px] font-bold truncate max-w-[70px]">{discordUser.username}</span>
                      <ChevronDown className="h-3 w-3 flex-shrink-0 text-slate-400" />
                    </button>
                    {showUserMenu && (
                      <div className="absolute left-0 top-full mt-1 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-[999] min-w-[120px]">
                        <div className="px-3 py-2 border-b border-slate-700">
                          <p className="text-white text-xs font-semibold truncate">{discordUser.username}</p>
                          <p className="text-slate-400 text-[10px]">Discord</p>
                        </div>
                        <button
                          onClick={handleLogout}
                          className="w-full text-left px-3 py-2 text-red-400 hover:bg-slate-700/50 text-xs font-medium transition-colors rounded-b-lg flex items-center space-x-2"
                        >
                          <LogIn className="h-3 w-3 rotate-180" />
                          <span>Log out</span>
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Centre: logo */}
            <div className="flex justify-center items-center">
              <img src="/cheeks_logo.svg" alt="Cheeks Logo" className="h-12 object-contain filter drop-shadow-md opacity-90" />
            </div>

            {/* Right: social icons */}
            <div className="flex items-center justify-end space-x-1 flex-1">
              <a href="https://cheeks.uk/docs/" className="group flex flex-col items-center w-10 relative">
                <div className="relative">
                  <img src="/discord.png" alt="Discord" className="h-8 w-8 object-contain transition-transform duration-200 group-hover:scale-105" />
                  {voiceCount > 0 && (
                    <div className="absolute -top-1 -right-2 text-white px-1 py-0.5 rounded-full flex items-center gap-0.5 shadow-sm" style={{backgroundColor:'#23a55a', border:'1px solid #1e293b'}}>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="w-2 h-2"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon><path d="M15.54 8.46a5 5 0 0 1 0 7.07"></path></svg>
                      <span className="font-medium leading-none" style={{ fontSize: '0.5rem' }}>{voiceCount}</span>
                    </div>
                  )}
                </div>
                {memberCount !== null && (
                  <div className="flex items-center justify-center space-x-0.5 bg-slate-900 border border-slate-700/50 rounded-full w-full py-0.5 shadow-lg mt-0.5">
                    <Users className="h-2 w-2 text-cyan-400 flex-shrink-0" />
                    <span className="text-slate-300 font-medium leading-none" style={{ fontSize: '0.5rem' }}>{formatCount(memberCount)}</span>
                  </div>
                )}
              </a>

              <a href="https://www.instagram.com/togecheeks" target="_blank" rel="noopener noreferrer" className="group flex flex-col items-center w-10">
                <img src="/instagram.png" alt="Instagram" className="h-8 w-8 object-contain transition-transform duration-200 group-hover:scale-105" />
                {instagramCount !== null && (
                  <div className="flex items-center justify-center space-x-0.5 bg-slate-900 border border-slate-700/50 rounded-full w-full py-0.5 shadow-lg mt-0.5">
                    <Users className="h-2 w-2 text-cyan-400 flex-shrink-0" />
                    <span className="text-slate-300 font-medium leading-none" style={{ fontSize: '0.5rem' }}>{instagramCount}</span>
                  </div>
                )}
              </a>

              <button onClick={() => setShowVideoModal(true)} className="group flex flex-col items-center w-10 relative">
                <div className="relative">
                  <img src="/wnlogo.png" alt="Whatnot Logo" className="h-8 w-8 object-contain transition-transform duration-200 group-hover:scale-105" />
                  {isLive && (
                    <span className="absolute -top-1 -right-1 flex h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500 border-2 border-slate-800"></span>
                    </span>
                  )}
                </div>
                {whatnotCount !== null && (
                  <div className="flex items-center justify-center space-x-0.5 bg-slate-900 border border-slate-700/50 rounded-full w-full py-0.5 shadow-lg mt-0.5">
                    <Users className="h-2 w-2 text-cyan-400 flex-shrink-0" />
                    <span className="text-slate-300 font-medium leading-none" style={{ fontSize: '0.5rem' }}>{whatnotCount}</span>
                  </div>
                )}
              </button>
            </div>

          </div>
        </div>
      </div>

      {/* ── TAB BAR ── */}
      <div className="bg-slate-900 sticky top-0 z-30">
        <div className="border-b border-slate-700/30">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between">
              <div className="flex">
              <button
                onClick={() => setActiveTab('profile')}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold transition-all duration-200 border-b-2 ${
                  activeTab === 'profile'
                    ? 'text-white'
: 'border-transparent text-slate-500 hover:text-slate-300'
                }`}
                style={activeTab === 'profile' ? { borderImage: 'linear-gradient(135deg, #22d3ee, #3b82f6) 1', borderBottomWidth: '2px', borderBottomStyle: 'solid' } : {}}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={activeTab === 'profile' ? 'url(#tab-grad)' : 'currentColor'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <defs><linearGradient id="tab-grad" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#22d3ee"/><stop offset="100%" stopColor="#3b82f6"/></linearGradient></defs>
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                </svg>
                Profile
              </button>
              <button
                onClick={() => setActiveTab('inventory')}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold transition-all duration-200 border-b-2 ${
                  activeTab === 'inventory'
                    ? 'text-white'
: 'border-transparent text-slate-500 hover:text-slate-300'
                }`}
                style={activeTab === 'inventory' ? { borderImage: 'linear-gradient(135deg, #22d3ee, #3b82f6) 1', borderBottomWidth: '2px', borderBottomStyle: 'solid' } : {}}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={activeTab === 'inventory' ? 'url(#tab-grad2)' : 'currentColor'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <defs><linearGradient id="tab-grad2" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#22d3ee"/><stop offset="100%" stopColor="#3b82f6"/></linearGradient></defs>
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/>
                </svg>
                Inventory
                <span className="text-xs opacity-50">({images.length})</span>
              </button>
              </div>

              {/* Right: Pokebubble watermark */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                <a href="https://pokebubble.com" target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.5rem', color: 'rgba(148,163,184,0.35)', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', whiteSpace: 'nowrap', textDecoration: 'none' }}>Powered by</a>
                <a href="https://pokebubble.com" target="_blank" rel="noopener noreferrer">
                  <img
                    src="/pb_logo.png"
                    alt="Pokebubble"
                    style={{ height: '32px', width: 'auto', opacity: 1, display: 'block' }}
                  />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Search bar — only shown in inventory, part of same sticky block */}
        {activeTab === 'inventory' && (
          <div className="bg-slate-900 border-b border-slate-700/50">
            <div className="max-w-7xl mx-auto px-4 py-3">
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 h-4 w-4" />
                  <input
                    type="text"
                    placeholder={`Search… ${currentText}|`}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    className="pl-9 pr-3 py-2.5 bg-slate-700/50 border border-slate-600 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-transparent w-full text-white placeholder-slate-500 text-sm transition-all duration-200"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <button
                  onClick={handleSearch}
                  className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white p-2.5 rounded-xl transition-all duration-200 active:scale-95 flex-shrink-0"
                >
                  <Search className="h-4 w-4" />
                </button>
              </div>

              {/* Category pills */}
              <div className="flex gap-2 mt-2.5 overflow-x-auto pb-0.5 scrollbar-hide">
                {categories.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    className={`px-3.5 py-1.5 rounded-xl font-medium transition-all duration-200 text-xs whitespace-nowrap flex-shrink-0 ${
                      selectedCategory === cat.id
                        ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg'
                        : 'bg-slate-700/50 text-slate-400 hover:text-white border border-slate-700/50'
                    }`}
                  >
                    {cat.name}
                    {cat.id !== 'all' && (
                      <span className="ml-1 opacity-70">({images.filter(img => img.category === cat.id).length})</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════════
          PROFILE TAB
      ══════════════════════════════════════════ */}
      {activeTab === 'profile' && (
        <main className="flex-1">
          <div className="max-w-lg mx-auto px-4 py-6 flex flex-col gap-4">

            {/* Avatar + name card */}
            <div className="bg-slate-800/60 backdrop-blur-sm border border-slate-700/50 rounded-2xl shadow-[0_6px_24px_rgba(0,0,0,0.4)] overflow-hidden">
              {/* Banner strip */}
              <div
                className="h-36 relative overflow-hidden"
                style={{ background: '#0c1a35' }}
              >
                <style>{`
                  @keyframes bannerShift {
                    0%   { background-position: 0% 50%; }
                    50%  { background-position: 100% 50%; }
                    100% { background-position: 0% 50%; }
                  }
                  .banner-anim {
                    position: absolute;
                    inset: 0;
                    background-image: url('https://rare-gallery.com/uploads/posts/1066121-illustration-anime-text-logo-cartoon-Pok-mon-Togepi-brand-ishmam-computer-wallpaper-font-organ.png');
                    background-size: cover;
                    background-position: center;
                  }
                  .banner-tint {
                    position: absolute;
                    inset: 0;
                    background: linear-gradient(160deg, rgba(12,26,53,0.72) 0%, rgba(10,58,92,0.65) 50%, rgba(13,42,74,0.72) 100%);
                  }
                  .banner-tint::after {
                    content: '';
                    position: absolute;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    height: 30%;
                    background: linear-gradient(to bottom, transparent, rgba(10,26,46,0.85));
                  }
                  @keyframes bannerBubbleFloat {
                    0%   { transform: translateY(0) translateX(0); opacity: 0; }
                    10%  { opacity: 0.7; }
                    80%  { opacity: 0.7; }
                    100% { transform: translateY(-200px) translateX(calc(8px * var(--bdrift))); opacity: 0; }
                  }
                  .banner-bubble {
                    position: absolute;
                    bottom: -20px;
                    border-radius: 50%;
                    background: radial-gradient(circle at 50% 50%,
                      rgba(255,255,255,0.04) 0%,
                      rgba(255,255,255,0.12) 60%,
                      rgba(255,255,255,0.35) 100%);
                    box-shadow:
                      inset 0 0 6px rgba(255,255,255,0.3),
                      inset 3px 0 6px rgba(180,100,255,0.25),
                      inset -3px 0 6px rgba(100,180,255,0.25),
                      inset 0 -3px 6px rgba(255,120,180,0.2);
                    mask-image: radial-gradient(circle, white 68%, transparent 71%);
                    -webkit-mask-image: radial-gradient(circle, white 68%, transparent 71%);
                    animation: bannerBubbleFloat linear infinite;
                    pointer-events: none;
                  }
                  .banner-bubble::after {
                    content: '';
                    position: absolute;
                    top: 18%;
                    left: 18%;
                    width: 22%;
                    height: 14%;
                    background: rgba(255,255,255,0.5);
                    border-radius: 50%;
                    transform: rotate(-35deg);
                    filter: blur(1px);
                  }
                `}</style>
                <div className="banner-anim" />
                <div className="banner-tint" />
                {/* Bubbles injected via ref */}
                <div ref={bannerBubbleRef} style={{position:'absolute',inset:0,pointerEvents:'none'}} />
                {/* Location + time */}
                <div style={{position:'absolute', top:'8px', right:'8px', display:'flex', alignItems:'center', pointerEvents:'none'}}>
                  <div style={{
                    display:'flex', alignItems:'center', gap:'4px',
                    background:'rgba(15,23,42,0.75)',
                    backdropFilter:'blur(6px)',
                    paddingLeft:'8px', paddingRight:'8px', paddingTop:'4px', paddingBottom:'4px',
                    borderRadius:'999px',
                    border:'1px solid rgba(51,65,85,0.5)',
                  }}>
                    <img src="https://flagcdn.com/gb.svg" alt="UK" style={{width:'12px',height:'12px',borderRadius:'50%',objectFit:'cover',flexShrink:0}} />
                    <span style={{color:'rgba(255,255,255,0.5)', fontSize:'0.55rem', fontWeight:500, letterSpacing:'0.02em', lineHeight:1, display:'block', paddingTop:'1px'}}>
                      London &nbsp;·&nbsp; {currentTime.toLocaleDateString('en-GB', {day:'numeric',month:'short',year:'numeric'})} &nbsp;{currentTime.toLocaleTimeString('en-GB', {hour:'2-digit',minute:'2-digit',second:'2-digit'})}
                    </span>
                  </div>
                </div>
              </div>

              <div className="px-4 pb-4 relative">
                {/* Avatar row */}
                <div className="flex items-start" style={{ marginTop: -36 }}>

                  {/* Avatar — left, relative so activity text can be absolute */}
                  <div className="relative flex-shrink-0">
                    {/* Thought bubble */}
                    {myStatus && (
                      <div style={{position: 'absolute', top: '-8px', left: '75px', pointerEvents: 'none'}}>
                        <div style={{background: '#1e293b', border: '1px solid rgba(51,65,85,0.5)', borderRadius: '12px', padding: '4px 10px', whiteSpace: 'nowrap', marginLeft: '14px', lineHeight: 1, display: 'inline-flex', alignItems: 'center'}}>
                          <span style={{color: '#cbd5e1', fontSize: '0.6rem', fontWeight: 500, display: 'block'}}>{myStatus}</span>
                        </div>
                        <div style={{width: '8px', height: '8px', borderRadius: '50%', background: '#1e293b', border: '1px solid rgba(51,65,85,0.5)', marginTop: '3px', marginLeft: '8px'}}></div>
                      </div>
                    )}
                    <div className="w-20 h-20 rounded-full overflow-hidden bg-slate-800 flex items-center justify-center shadow-xl" style={{border: '1px solid rgba(51,65,85,0.5)'}}>
                      <img src="/pfp.png" alt="Profile" className="w-full h-full object-cover" />
                    </div>
                    {/* Presence status dot */}
                    {(() => {
                      const s = myPresence || 'offline';
                      const dotColor = s === 'online' ? '#23a55a' : s === 'idle' ? '#f0b232' : s === 'dnd' ? '#f23f43' : '#80848e';
                      if (s === 'idle') return (
                        <svg
                          className="absolute bottom-0.5 right-0.5"
                          width="20" height="20" viewBox="0 0 20 20"
                          title="Idle"
                        >
                          <circle cx="10" cy="10" r="10" fill="#1e293b"/>
                          <circle cx="10" cy="10" r="6.5" fill="#f0b232"/>
                          <circle cx="7.5" cy="7.5" r="5" fill="#1e293b"/>
                        </svg>
                      );
                      return (
                        <div
                          className="absolute bottom-0.5 right-0.5 w-5 h-5 rounded-full flex items-center justify-center"
                          style={{ background: dotColor, border: '3.5px solid #1e293b' }}
                          title={s === 'dnd' ? 'Do Not Disturb' : s.charAt(0).toUpperCase() + s.slice(1)}
                        >
                          {s === 'dnd' && <div style={{width:'55%',height:'2px',background:'white',borderRadius:2}} />}
                        </div>
                      );
                    })()}
                  </div>

                  {/* Activity info — top right, absolutely positioned, out of flow */}
                  {myActivity && (
                    <div style={{position: 'absolute', right: '16px', top: '44px', display: 'flex', flexDirection: 'column', gap: '1px', lineHeight: '1.2', whiteSpace: 'nowrap', alignItems: 'flex-end'}}>
                      <div className="text-slate-500 text-[9px] font-semibold uppercase tracking-wider">{myActivity.type}</div>
                      <div className="text-slate-500 text-[9px] font-medium">{myActivity.name}</div>
                      {myActivity.detail && <div className="text-slate-500 text-[9px]">{myActivity.detail}</div>}
                      {myActivity.state && !elapsed && <div className="text-slate-500 text-[9px]">{myActivity.state}</div>}
                      {elapsed && <div style={{display:'flex', alignItems:'center', gap:'2px', color:'#23a55a', fontSize:'0.6rem', fontWeight:500, lineHeight:1, marginTop:'2px'}}>
                        {myActivity.type === 'Watching' && <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#23a55a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>}
                        {myActivity.type === 'Playing' && <img src="/controller.svg" width="9" height="9" style={{flexShrink:0, filter:'brightness(0) saturate(100%) invert(52%) sepia(98%) saturate(346%) hue-rotate(95deg) brightness(93%) contrast(92%)'}} />}
                        {myActivity.type === 'Listening' && <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#23a55a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>}
                        {myActivity.type === 'Streaming' && <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#23a55a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><path d="M23 7l-7 5 7 5V7z"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>}
                        {!['Watching','Playing','Listening','Streaming'].includes(myActivity.type) && <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#23a55a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/></svg>}
                        {elapsed}
                      </div>}
                    </div>
                  )}

                  {/* Badge row — sits to the right of the avatar, aligned to its bottom */}
                  <div className="flex items-end gap-1.5 ml-3 pb-0.5" style={{marginTop: 'auto'}}>
                    {badges.map((badge) => (
                      <div
                        key={badge.key}
                        title={badge.label}
                        className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center shadow-xl overflow-hidden"
                        style={{border: '1px solid rgba(51,65,85,0.5)'}}
                      >
                        <img src={badge.img} alt={badge.label} className="w-6 h-6 object-contain" />
                      </div>
                    ))}
                  </div>

                </div>

                {/* Name + bio */}
                <div className="mt-3">
                  <div className="flex items-center gap-1.5">
                    <h1 className="text-white font-bold text-xl leading-tight">cheeks</h1>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="flex-shrink-0 mt-0.5">
                      <defs>
                        <linearGradient id="checkGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#22d3ee"/>
                          <stop offset="100%" stopColor="#3b82f6"/>
                        </linearGradient>
                      </defs>
                      <circle cx="12" cy="12" r="10" fill="url(#checkGrad)"/>
                      <path d="M7.5 12.5l3 3 6-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <p className="text-xs font-medium mt-0.5 mb-2" style={{ background: 'linear-gradient(135deg, #22d3ee, #3b82f6)', WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent', color: 'transparent' }}>@togecheeks</p>
                  <p className="text-slate-300 leading-relaxed" style={{maxWidth: 'calc(100% - 80px)', fontSize: 'clamp(0.6rem, 2.5vw, 0.75rem)', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden'}}>
                    Everything Pokémon — cards, collectibles & more ✨
                  </p>
                </div>

                {/* Corner inventory button — bottom right of card content */}
                <button
                  onClick={() => setActiveTab('inventory')}
                  className="absolute bottom-0 right-0 flex flex-col items-center justify-center gap-0.5 border-t border-l border-cyan-400/30 rounded-tl-xl px-3 py-2 transition-all duration-200 active:scale-95"
                  style={{background: 'linear-gradient(135deg, #22d3ee, #3b82f6)'}}
                >
                  <Package className="h-4 w-4 text-white" />
                  <span className="text-white font-medium" style={{fontSize:'0.55rem', lineHeight:1}}>Browse Inventory</span>
                </button>

              </div>
            </div>

            {/* Links + Updating panel */}
            <div className="flex gap-3">

              {/* Left: all 5 links */}
              <div className="flex flex-col gap-2.5" style={{width: '50%'}}>

              {/* Discord */}
              <a href="https://cheeks.uk/docs/" className="relative flex items-center gap-3 bg-slate-800/60 backdrop-blur-sm border border-slate-700/50 rounded-2xl shadow-[0_6px_24px_rgba(0,0,0,0.4)] px-4 py-2.5 active:scale-[0.98] transition-all duration-150">
                  <div className="absolute left-0 top-3 bottom-3 w-[3px] rounded-r-full flex-shrink-0" style={{background:'#5865F2'}} />
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                    <div className="relative">
                      <img src="/discord.png" alt="Discord" className="w-6 h-6 object-contain" />
                      {voiceCount > 0 && (
                        <div className="absolute -top-1 -right-2 text-white px-1 py-0.5 rounded-full flex items-center gap-0.5 shadow-sm" style={{backgroundColor:'#23a55a', border:'1px solid #1e293b'}}>
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="w-2 h-2"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon><path d="M15.54 8.46a5 5 0 0 1 0 7.07"></path></svg>
                          <span className="font-medium leading-none" style={{fontSize:'0.5rem'}}>{voiceCount}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="text-white font-semibold text-sm leading-none">Discord</div>
                  <div className="absolute right-3 top-0 bottom-0 flex flex-col justify-between items-end py-2">
                    <div className="flex items-center gap-0.5 text-slate-500 font-medium" style={{fontSize:'0.6rem', lineHeight:1}}>
                      <Users className="h-2 w-2 flex-shrink-0" />
                      {memberCount !== null ? formatCount(memberCount) : '—'}
                    </div>
                    {voiceCount > 0 ? (
                      <div className="flex items-center gap-0.5 font-medium" style={{fontSize:'0.6rem', lineHeight:1, color:'#23a55a'}}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="8" height="8"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>
                        {voiceCount} in voice
                      </div>
                    ) : (
                      <div className="flex items-center gap-0.5 font-medium" style={{fontSize:'0.6rem', lineHeight:1, color:'#64748b'}}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="8" height="8"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>
                        0 in voice
                      </div>
                    )}
                    <span className="text-slate-500 font-medium" style={{fontSize:'0.6rem', lineHeight:1}}>discord.cheeks.uk</span>
                  </div>
                </a>

              {/* Instagram */}
              <a href="https://www.instagram.com/togecheeks" target="_blank" rel="noopener noreferrer" className="relative flex items-center gap-3 bg-slate-800/60 backdrop-blur-sm border border-slate-700/50 rounded-2xl shadow-[0_6px_24px_rgba(0,0,0,0.4)] px-4 py-2.5 active:scale-[0.98] transition-all duration-150">
                <div className="absolute left-0 top-3 bottom-3 w-[3px] rounded-r-full flex-shrink-0" style={{background:'#E1306C'}} />
                <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0">
                  <img src="/instagram.png" alt="Instagram" className="w-6 h-6 object-contain" />
                </div>
                <div className="text-white font-semibold text-sm leading-none">Instagram</div>
                <div className="absolute right-3 top-0 bottom-0 flex flex-col justify-between items-end py-2">
                  <div className="flex items-center gap-0.5 text-slate-500 font-medium" style={{fontSize:'0.6rem', lineHeight:1}}>
                    <Users className="h-2 w-2 flex-shrink-0" />
                    {instagramCount !== null ? formatCount(instagramCount) : '—'}
                  </div>
                  <span className="text-slate-500 font-medium" style={{fontSize:'0.6rem', lineHeight:1}}>&nbsp;</span>
                  <span className="text-slate-500 font-medium" style={{fontSize:'0.6rem', lineHeight:1}}>@togecheeks</span>
                </div>
              </a>

              {/* Whatnot */}
              <button onClick={() => setShowVideoModal(true)} className="relative w-full flex items-center gap-3 bg-slate-800/60 backdrop-blur-sm border border-slate-700/50 rounded-2xl shadow-[0_6px_24px_rgba(0,0,0,0.4)] px-4 py-2.5 active:scale-[0.98] transition-all duration-150">
                <div className="absolute left-0 top-3 bottom-3 w-[3px] rounded-r-full flex-shrink-0" style={{background:'#FADC00'}} />
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <div className="relative">
                    <img src="/wnlogo.png" alt="Whatnot" className="w-6 h-6 object-contain" />
                    {isLive && (
                      <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500 border-2 border-slate-800"></span></span>
                    )}
                  </div>
                </div>
                <div className="text-white font-semibold text-sm leading-none text-left">Whatnot</div>
                <div className="absolute right-3 top-0 bottom-0 flex flex-col justify-between items-end py-2">
                  <div className="flex items-center gap-0.5 text-slate-500 font-medium" style={{fontSize:'0.6rem', lineHeight:1}}>
                    <Users className="h-2 w-2 flex-shrink-0" />
                    {whatnotCount !== null ? formatCount(whatnotCount) : '—'}
                  </div>
                  {isLive ? (
                    <div className="flex items-center gap-1 text-red-500 font-medium" style={{fontSize:'0.6rem', lineHeight:1}}>
                      <span className="relative flex h-1.5 w-1.5 flex-shrink-0"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span><span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-red-500"></span></span>
                      LIVE NOW
                    </div>
                  ) : (
                    <span className="text-slate-500 font-medium" style={{fontSize:'0.6rem', lineHeight:1}}>Offline</span>
                  )}
                  <span className="text-slate-500 font-medium" style={{fontSize:'0.6rem', lineHeight:1}}>@togecheeks</span>
                </div>
              </button>

              {/* Pokémon GO */}
              <div className="relative flex items-center gap-3 bg-slate-800/60 backdrop-blur-sm border border-slate-700/50 rounded-2xl shadow-[0_6px_24px_rgba(0,0,0,0.4)] px-4 py-2.5 cursor-pointer active:scale-[0.98] transition-all duration-150" onClick={() => { navigator.clipboard?.writeText('664463929998'); setShowPogoModal(true); }}>
                <div className="absolute left-0 top-3 bottom-3 w-[3px] rounded-r-full flex-shrink-0" style={{background:'#030CA6'}} />
                <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0">
                  <img src="/pogo.png" alt="Pokémon GO" className="w-6 h-6 object-contain" />
                </div>
                <div className="text-white font-semibold text-sm leading-none">Pokémon GO</div>
                <div className="absolute right-3 top-0 bottom-0 flex flex-col justify-between items-end py-2">
                  <span className="text-slate-500 font-medium" style={{fontSize:'0.6rem', lineHeight:1}}>Lv. 70</span>
                  <span className="text-slate-500 font-medium" style={{fontSize:'0.6rem', lineHeight:1}}>Valor</span>
                  <span className="text-slate-500 font-medium" style={{fontSize:'0.6rem', lineHeight:1}}>togecheeks</span>
                </div>
              </div>

              {/* Steam */}
              <a href="https://steamcommunity.com/id/togecheeks/" target="_blank" rel="noopener noreferrer" className="relative flex items-center gap-3 bg-slate-800/60 backdrop-blur-sm border border-slate-700/50 rounded-2xl shadow-[0_6px_24px_rgba(0,0,0,0.4)] px-4 py-2.5 active:scale-[0.98] transition-all duration-150">
                <div className="absolute left-0 top-3 bottom-3 w-[3px] rounded-r-full flex-shrink-0" style={{background:'#0B79AC'}} />
                <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0">
                  <img src="/steam.png" alt="Steam" className="w-6 h-6 object-contain" />
                </div>
                <div className="text-white font-semibold text-sm leading-none">Steam</div>
                <div className="absolute right-3 top-0 bottom-0 flex flex-col justify-between items-end py-2">
                  <span className="text-slate-500 font-medium" style={{fontSize:'0.6rem', lineHeight:1}}>&nbsp;</span>
                  <span className="text-slate-500 font-medium" style={{fontSize:'0.6rem', lineHeight:1}}>&nbsp;</span>
                  <span className="text-slate-500 font-medium" style={{fontSize:'0.6rem', lineHeight:1}}>cheeks</span>
                </div>
              </a>

              </div>{/* end left col */}

              {/* Right: Favourites + Searching + Updating */}
              <div className="flex-1 rounded-2xl border border-slate-700/50 bg-slate-800/60 shadow-[0_6px_24px_rgba(0,0,0,0.4)] flex flex-col p-3 gap-3 self-stretch">

                {/* Favourite Pokemon */}
                <div>
                  <div className="text-slate-400 text-[10px] font-semibold uppercase tracking-wider mb-2">Favourites</div>
                  <div className="flex gap-2">
                    {/* Fav 1 */}
                    <div className="flex-1 flex flex-col items-center gap-1">
                      <div className="w-full aspect-square rounded-xl bg-slate-900/80 border border-slate-700/50 flex items-center justify-center overflow-hidden shadow-[inset_0_6px_24px_rgba(0,0,0,0.4)]">
                        <img
                          src="/togepi.png"
                          alt="Togepi"
                          className="w-full h-full object-contain p-1.5"
                          onError={(e) => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }}
                        />
                        <div className="hidden w-full h-full items-center justify-center">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-slate-600"><circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/></svg>
                        </div>
                      </div>
                    </div>
                    {/* Fav 2 */}
                    <div className="flex-1 flex flex-col items-center gap-1">
                      <div className="w-full aspect-square rounded-xl bg-slate-900/80 border border-slate-700/50 flex items-center justify-center overflow-hidden shadow-[inset_0_6px_24px_rgba(0,0,0,0.4)]">
                        <img
                          src="/lugia.png"
                          alt="Lugia"
                          className="w-full h-full object-contain p-1.5"
                          onError={(e) => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }}
                        />
                        <div className="hidden w-full h-full items-center justify-center">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-slate-600"><circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/></svg>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Divider */}
                <div className="border-t border-slate-700/40" />

                {/* Searching */}
                <div className="flex-1 flex flex-col min-h-0">
                  <div className="text-slate-400 text-[10px] font-semibold uppercase tracking-wider mb-2">Hunting</div>
                  <div className="flex-1 relative rounded-xl bg-slate-900/80 border border-slate-700/50 overflow-hidden min-h-0 shadow-[inset_0_6px_24px_rgba(0,0,0,0.4)]">
                    <img
                      src="/gbmini.png"
                      alt="Searching for"
                      className="absolute inset-0 w-full h-full object-contain px-1 pt-1 pb-7"
                      onError={(e) => { e.target.style.display='none'; }}
                    />
                    {/* Text sits at the very bottom inside the box */}
                    <div className="absolute bottom-0 left-0 right-0 px-2 py-1.5 text-center">
                      <span className="text-slate-400 text-[9px] font-medium leading-tight" style={{display:'block'}}>Togepi Holo Prism</span>
                      <span className="text-slate-500 text-[8px] font-medium leading-tight" style={{display:'block'}}>GB Pocket N64 Stadium Collection</span>
                    </div>
                  </div>
                </div>

              </div>

            </div>

            {/* Fun Fact Panel */}
            {funFact && (
              <div className="bg-slate-800/60 backdrop-blur-sm border border-slate-700/50 rounded-2xl shadow-[0_6px_24px_rgba(0,0,0,0.4)] px-4 py-3 flex items-start gap-3">
                <img src="/pball.svg" alt="" className="h-4 w-4 flex-shrink-0 mt-0.5" style={{filter:'brightness(0) saturate(100%) invert(43%) sepia(8%) saturate(869%) hue-rotate(175deg) brightness(95%) contrast(87%)'}} />
                <div className="min-w-0">
                  <div className="text-slate-400 text-[10px] font-semibold uppercase tracking-wider mb-1">Pokémon Fact</div>
                  <p className="text-slate-300 leading-relaxed" style={{fontSize:'0.7rem'}}>{funFact}</p>
                </div>
              </div>
            )}

          </div>
        </main>
      )}

      {/* ══════════════════════════════════════════
          INVENTORY TAB
      ══════════════════════════════════════════ */}
      {activeTab === 'inventory' && (
        <main className="flex-1">

          {/* Grid */}
          <div className="max-w-7xl mx-auto px-3 py-4">
            {filteredImages.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                {filteredImages.map((image) => (
                  <div
                    key={image.id}
                    className="group bg-slate-800/90 backdrop-blur-sm rounded-xl border border-slate-700/50 hover:border-cyan-400/50 active:border-cyan-400/50 transition-all duration-300 hover:shadow-xl hover:shadow-cyan-400/10 overflow-hidden cursor-pointer shadow-[0_4px_16px_rgba(0,0,0,0.25)]"
                  >
                    <div className="aspect-square bg-slate-700/50 flex items-center justify-center relative overflow-hidden">
                      <img
                        src={`/${image.folder}/${image.filename}`}
                        alt={image.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                        onLoad={(e) => {
                          if (e.target.nextSibling) e.target.nextSibling.style.display = 'none';
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-br from-slate-700 via-slate-600 to-slate-700 flex items-center justify-center" style={{ display: 'none' }}>
                        <div className="text-center p-4">
                          <div className="w-16 h-16 mx-auto mb-2 opacity-30">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="text-slate-500">
                              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" strokeWidth="2"/>
                              <circle cx="8.5" cy="8.5" r="1.5" strokeWidth="2"/>
                              <polyline points="21 15 16 10 5 21" strokeWidth="2"/>
                            </svg>
                          </div>
                          <p className="text-xs text-slate-500 font-medium">Image not found</p>
                        </div>
                      </div>
                      {image.grade && (
                        <div className="absolute top-2 left-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-2 py-1 rounded-md text-xs font-bold shadow-lg backdrop-blur-sm border border-cyan-400/50">
                          {image.grade}
                        </div>
                      )}
                    </div>
                    <div className="p-2.5">
                      <h3 className="text-xs font-semibold text-white mb-1 group-hover:text-cyan-400 transition-colors leading-tight">
                        {image.name}
                      </h3>
                      {image.set.split('  ').map((line, idx) => (
                        <p key={idx} className="text-[10px] text-slate-400 leading-tight">{line}</p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-12 text-center">
                <p className="text-slate-400">No items found matching your search.</p>
              </div>
            )}
          </div>
        </main>
      )}

      {/* ── FOOTER ── */}
      <footer className="bg-slate-800/80 backdrop-blur-lg border-t border-slate-700/50">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex flex-col items-center gap-2">
            <div className="flex items-center justify-center gap-3">
              <a href="https://cheeks.uk/docs/" className="group">
                <img src="/discord.png" alt="Discord" className="h-6 w-6 object-contain opacity-50 group-hover:opacity-100 transition-opacity duration-200" />
              </a>
              <a href="https://www.instagram.com/togecheeks" target="_blank" rel="noopener noreferrer" className="group">
                <img src="/instagram.png" alt="Instagram" className="h-6 w-6 object-contain opacity-50 group-hover:opacity-100 transition-opacity duration-200" />
              </a>
              <button onClick={() => setShowVideoModal(true)} className="group">
                <img src="/wnlogo.png" alt="Whatnot" className="h-6 w-6 object-contain opacity-50 group-hover:opacity-100 transition-opacity duration-200" />
              </button>
            </div>
            <span className="text-slate-600 text-xs">© 2026 PokéBubble</span>
          </div>
        </div>
      </footer>

      {/* ── WHATNOT VIDEO MODAL ── */}
      {showVideoModal && (
        <div className="fixed inset-0 flex items-center justify-center p-4 z-[200] pointer-events-none">
          <div className="relative bg-slate-800/95 backdrop-blur-xl rounded-2xl w-full max-w-md max-h-[95vh] overflow-hidden border border-slate-700/50 shadow-2xl shadow-black/50 pointer-events-auto">
            <button
              onClick={handleVideoModalClose}
              className="absolute top-4 right-4 z-10 w-10 h-10 bg-slate-700/80 hover:bg-slate-600/80 text-slate-300 hover:text-white rounded-full flex items-center justify-center transition-colors duration-200 backdrop-blur-sm border border-slate-600/50"
            >
              <X className="h-5 w-5" />
            </button>
            <div className="relative">
              <video
                src="/splash.mp4"
                autoPlay
                muted
                preload="auto"
                onEnded={handleVideoEnd}
                className="w-full h-auto rounded-2xl object-cover"
                style={{ aspectRatio: '9/16' }}
              >
                Your browser does not support the video tag.
              </video>
              <div className={`absolute top-6 left-4 right-4 flex justify-center transition-all duration-1000 ease-out ${videoEnded ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'}`}>
                <div className="bg-white rounded-2xl overflow-hidden shadow-2xl w-full max-w-sm">
                  {/* Banner image with stats bar overlaid at bottom, right-aligned so avatar doesn't hide them */}
                  <div className="relative h-36 overflow-hidden">
                    <img src="https://rare-gallery.com/uploads/posts/1066121-illustration-anime-text-logo-cartoon-Pok-mon-Togepi-brand-ishmam-computer-wallpaper-font-organ.png" alt="Banner" className="w-full h-full object-cover object-center" />
                    {/* Stats bar pinned to bottom-right */}
                    <div className="absolute bottom-0 left-0 right-0 bg-black/80 px-3 py-1.5 flex items-center justify-end gap-3">
                      <span className="flex items-center gap-1 text-white text-xs font-semibold"><span className="text-yellow-400">★</span>5.0</span>
                      <span className="text-white/30">|</span>
                      <span className="text-white/60 text-xs">Reviews <span className="text-white font-semibold">182</span></span>
                      <span className="text-white/30">|</span>
                      <span className="text-white/60 text-xs">Sold <span className="text-white font-semibold">1.1k</span></span>
                    </div>
                  </div>
                  {/* Avatar sitting on the border between banner and white, not clipped by overflow-hidden */}
                  <div className="relative px-4">
                    <div className="absolute -top-7 left-4 w-14 h-14 rounded-full border-[3px] border-white overflow-hidden bg-white shadow-xl">
                      <img src="/pfp.png" alt="Profile" className="w-full h-full object-cover" />
                    </div>
                    {/* Button top-right of white area */}
                    <div className="flex justify-end pt-2">
                      <a href="https://www.whatnot.com/en-GB/user/togecheeks/shows" target="_blank" rel="noopener noreferrer" onClick={(e) => { e.stopPropagation(); window.open('https://www.whatnot.com/en-GB/user/togecheeks/shows', '_blank'); e.preventDefault(); }} className={`flex items-center gap-1.5 text-white px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 active:scale-95 shadow-md flex-shrink-0 ${isLive ? 'bg-red-500 hover:bg-red-600' : 'bg-black hover:bg-slate-800'}`}>
                        {isLive ? (
                          <>
                            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse inline-block"></span>
                            Watch Now
                          </>
                        ) : (
                          <>
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                            View Profile
                          </>
                        )}
                      </a>
                    </div>
                    {/* Name + handle */}
                    <div className="pt-1 pb-3">
                      <div className="flex items-center gap-1.5">
                        <span className="text-black font-bold text-lg leading-none">cheeks</span>
                        {isLive ? (
                          <span className="flex items-center gap-1 bg-red-500 text-white text-[0.55rem] font-semibold px-1.5 py-0.5 rounded-full leading-none">
                            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse inline-block"></span>LIVE
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 bg-slate-200 text-slate-500 text-[0.55rem] font-semibold px-1.5 py-0.5 rounded-full leading-none">OFFLINE</span>
                        )}
                      </div>
                      <span className="text-black/60 text-xs font-medium leading-none block mt-0.5">@togecheeks</span>
                      <p className="text-slate-500 text-[0.65rem] leading-relaxed mt-1.5">Everything Pokémon — cards, collectibles & more ✨</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className={`absolute bottom-6 left-4 right-4 transition-all duration-1000 ease-out ${videoEnded ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`}>
                <div className="space-y-3">
                  <a href="https://www.whatnot.com/user/togecheeks/shows" target="_blank" rel="noopener noreferrer" className="w-full rounded-full p-4 flex items-center space-x-4 hover:opacity-80 transition-all duration-200 transform hover:scale-[1.02] shadow-lg" style={{ backgroundColor: '#272527' }}>
                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#FADC00' }}><Bookmark className="h-5 w-5 text-black" /></div>
                    <span className="text-white font-medium text-sm">Bookmark the next Livestreams</span>
                  </a>
                  <a href="https://whatnot.com/invite/togecheeks" target="_blank" rel="noopener noreferrer" className="w-full rounded-full p-4 flex items-center space-x-4 hover:opacity-80 transition-all duration-200 transform hover:scale-[1.02] shadow-lg" style={{ backgroundColor: '#272527' }}>
                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#FADC00' }}><Gift className="h-5 w-5 text-black" /></div>
                    <span className="text-white font-medium text-sm">Get £10 FREE Pokemon Cards</span>
                  </a>
                  <a href="https://whatnot.com/invite/seller/togecheeks" target="_blank" rel="noopener noreferrer" className="w-full rounded-full p-4 flex items-center space-x-4 hover:opacity-80 transition-all duration-200 transform hover:scale-[1.02] shadow-lg" style={{ backgroundColor: '#272527' }}>
                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#FADC00' }}><DollarSign className="h-5 w-5 text-black" /></div>
                    <span className="text-white font-medium text-sm">Become a Seller and get £150 FREE</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── POKÉMON GO MODAL ── */}
      {showPogoModal && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4" style={{background:'rgba(0,0,0,0.7)', backdropFilter:'blur(4px)'}} onClick={() => setShowPogoModal(false)}>
          <div className="bg-slate-800 border border-slate-700/50 rounded-2xl shadow-2xl w-full max-w-xs p-5" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <img src="/pogo.png" alt="Pokémon GO" className="w-7 h-7 object-contain" />
                <span className="text-white font-bold text-base">Pokémon GO</span>
              </div>
              <button onClick={() => setShowPogoModal(false)} className="text-slate-400 hover:text-white transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>
            {/* Trainer info */}
            <div className="bg-slate-900/60 rounded-xl border border-slate-700/50 p-3 mb-4 flex flex-col gap-1.5">
              <div className="flex items-center justify-between">
                <span className="text-slate-400 font-medium" style={{fontSize:'0.7rem'}}>Trainer Name</span>
                <span className="text-white font-semibold" style={{fontSize:'0.7rem'}}>togecheeks</span>
              </div>
              <div className="border-t border-slate-700/40" />
              <div className="flex items-center justify-between">
                <span className="text-slate-400 font-medium" style={{fontSize:'0.7rem'}}>Trainer Code</span>
                <span className="text-white font-semibold font-mono" style={{fontSize:'0.7rem'}}>6644 6392 9998</span>
              </div>
            </div>
            {/* QR code */}
            <div className="flex flex-col items-center gap-2">
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=664463929998&bgcolor=1e293b&color=ffffff&margin=10`}
                alt="QR Code"
                className="rounded-xl w-44 h-44"
              />
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default CardExplorer;